/**
 * Environment variables which an SMK client can inject values for as global variables via Script at Startup
 *
 * These env vars are set in window.__ENV for
 * runtime configuration, which are injected via the env_config.ts file
 */
export enum EnvVar {
  NEXT_PUBLIC_API_BASE_URL = 'NEXT_PUBLIC_API_BASE_URL',
  NEXT_PUBLIC_BASE_NEXT_URL = 'NEXT_PUBLIC_BASE_NEXT_URL',
  NEXT_PUBLIC_FILE_UPLOAD_BASE_URL = 'NEXT_PUBLIC_FILE_UPLOAD_BASE_URL',
  NEXT_PUBLIC_PUSHER_CLUSTER = 'NEXT_PUBLIC_PUSHER_CLUSTER',
  NEXT_PUBLIC_PUSHER_KEY = 'NEXT_PUBLIC_PUSHER_KEY',
  NEXT_PUBLIC_SLACK_CLIENT_ID = 'NEXT_PUBLIC_SLACK_CLIENT_ID',
  NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN = 'NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN',
  NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL = 'NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL'
}

const OPTIONAL_NEXT_PUBLIC_ENV_VARS = [
  EnvVar.NEXT_PUBLIC_PUSHER_CLUSTER,
  EnvVar.NEXT_PUBLIC_PUSHER_KEY,
  EnvVar.NEXT_PUBLIC_SLACK_CLIENT_ID
] as const satisfies readonly EnvVar[];

export function isOptionalNextPublicEnvVar(
  envVar: EnvVar
): envVar is OptionalNextPublicEnvVar {
  return OPTIONAL_NEXT_PUBLIC_ENV_VARS.includes(
    envVar as OptionalNextPublicEnvVar
  );
}

type OptionalNextPublicEnvVar = (typeof OPTIONAL_NEXT_PUBLIC_ENV_VARS)[number];

/**
 * Interface for environment configuration that ensures all enum values
 * from EnvConfigVar are included as keys with string values
 */
export type EnvConfig = {
  [K in EnvVar]: K extends OptionalNextPublicEnvVar
    ? string | undefined
    : string;
};

/**
 * Checks if an environment variable value is valid based on whether it's required or optional
 * For required environment variables, the value must be non-undefined
 * For optional environment variables, both undefined and string values are valid
 *
 * @param envVar The environment variable key to check
 * @param value The environment variable value to validate
 * @returns True if the value is valid for the given environment variable, false otherwise
 */

export function isValidEnvConfigValue<K extends EnvVar>(
  envVar: K,
  value: string | undefined
): value is EnvConfig[K] {
  if (isOptionalNextPublicEnvVar(envVar)) {
    return true;
  }

  return value !== undefined;
}
